import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

export function usePreventNavigationNative({ hasUnsavedChanges }) {
    const suppressRef = useRef(false);

    useEffect(() => {
        const handleBeforeUnload = e => {
            if (!hasUnsavedChanges || suppressRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const enableNavigation = useCallback(() => {
        suppressRef.current = true;

        // Reset back to false after the event loop tick
        setTimeout(() => {
            suppressRef.current = false;
        }, 500);
    }, []);

    return { enableNavigation };
}

const PreventNavigationContext = createContext(null);

export function PreventNavigationProvider({ hasUnsavedChanges, children }) {
    const { enableNavigation } = usePreventNavigationNative({ hasUnsavedChanges });

    const contextValue = useMemo(() => ({ enableNavigation }), [enableNavigation]);

    return <PreventNavigationContext.Provider value={contextValue}>{children}</PreventNavigationContext.Provider>;
}

PreventNavigationProvider.propTypes = {
    children: PropTypes.node,
    hasUnsavedChanges: PropTypes.bool
};

export function usePreventNavigationContext() {
    const context = useContext(PreventNavigationContext);
    if (!context) {
        throw new Error('usePreventNavigation must be used within a PreventNavigationProvider');
    }
    return context;
}
